<template>
  <div class="support-shell">
    <div class="support-wrap">
      <!-- ============ hero ============ -->
      <div class="support-hero">
        <span class="hero-rail"></span>
        <div class="hero-badge"><v-icon size="24">mdi-lifebuoy</v-icon></div>
        <div class="hero-copy">
          <div class="hero-eyebrow">Sarpa Odisha</div>
          <h1 class="hero-title">Support</h1>
          <p class="hero-lead">
            Help with snake rescue, awareness and anything else in the app.
            Below is how Sarpa works and how to reach us.
          </p>
        </div>
        <a class="hero-mail" :href="'mailto:' + supportEmail">
          <v-icon size="16">mdi-email-outline</v-icon>
          {{ supportEmail }}
        </a>
      </div>

      <!-- ============ what the app does ============ -->
      <div class="panel">
        <div class="panel-head">
          <div class="panel-icon"><v-icon size="19">mdi-apps</v-icon></div>
          <div class="panel-copy">
            <div class="cardtitle">App Features</div>
            <div class="subtext">What you can do with Sarpa</div>
          </div>
        </div>
        <div class="feature-grid">
          <div class="feature-cell" v-for="(f, i) in features" :key="i">
            <div class="feature-icon"><v-icon size="17">{{ f.icon }}</v-icon></div>
            <div class="feature-body">
              <div class="feature-name">{{ f.name }}</div>
              <div class="feature-text">{{ f.text }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- ============ getting help ============ -->
      <div class="panel">
        <div class="panel-head">
          <div class="panel-icon"><v-icon size="19">mdi-help-circle-outline</v-icon></div>
          <div class="panel-copy">
            <div class="cardtitle">How to Get Support</div>
            <div class="subtext">Two ways to find an answer</div>
          </div>
        </div>
        <div class="help-grid">
          <div class="help-cell">
            <div class="feature-icon"><v-icon size="17">mdi-book-open-page-variant-outline</v-icon></div>
            <div class="feature-body">
              <div class="feature-name">In-App Help Center</div>
              <div class="feature-text">
                FAQs, guides and step-by-step instructions on using Sarpa.
              </div>
            </div>
          </div>
          <div class="help-cell">
            <div class="feature-icon"><v-icon size="17">mdi-email-fast-outline</v-icon></div>
            <div class="feature-body">
              <div class="feature-name">Contact Support</div>
              <div class="feature-text">Write to us and we'll get back to you.</div>
              <!-- on its own line as a chip: inline, the address was breaking
                   mid-word into "…gmail.co / m" -->
              <a class="mail-chip" :href="'mailto:' + supportEmail">
                <v-icon size="14">mdi-email-outline</v-icon>
                <span class="mail-text">{{ supportEmail }}</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- ============ the rest ============ -->
      <div class="panel">
        <div class="panel-head">
          <div class="panel-icon"><v-icon size="19">mdi-information-outline</v-icon></div>
          <div class="panel-copy">
            <div class="cardtitle">Good to Know</div>
            <div class="subtext">Privacy, updates, feedback and conduct</div>
          </div>
        </div>
        <div class="note-grid">
          <div class="note-cell" v-for="(n, i) in notes" :key="i">
            <div class="note-head">
              <v-icon size="16">{{ n.icon }}</v-icon>
              <span class="note-name">{{ n.name }}</span>
            </div>
            <p class="note-text">{{ n.text }}</p>
          </div>
        </div>
      </div>

      <p class="closing">
        Thank you for using Sarpa and being part of the solution to reduce
        snake&ndash;human conflicts. We appreciate your commitment to snake
        conservation and public safety.
      </p>
    </div>
  </div>
</template>

<script>
/*
 * Rewritten off <b-row>/<b-col>: bootstrap-vue is not a dependency of this
 * project, so those tags resolved to unknown elements with no grid behind
 * them - which is why the copy ran the full width of the window.
 */
export default {
  data() {
    return {
      supportEmail: "leopardtechlabs@gmail.com",
      features: [
        {
          icon: "mdi-snake",
          name: "Snake Rescue Requests",
          text: "Report a sighting and request help from local rescue teams for a safe, efficient rescue.",
        },
        {
          icon: "mdi-hospital-box-outline",
          name: "Snake Bite Assistance",
          text: "Find nearby hospitals stocking anti-venom, and what to do in a snake-bite emergency.",
        },
        {
          icon: "mdi-book-search-outline",
          name: "Snake Awareness Resources",
          text: "Learn the species, the part they play in the ecosystem, and how to co-exist safely.",
        },
        {
          icon: "mdi-account-heart-outline",
          name: "Volunteer Rescuers",
          text: "See the rescuers available in your area who can relocate snakes safely and humanely.",
        },
        {
          icon: "mdi-progress-clock",
          name: "Incident Tracker",
          text: "Follow a rescue request from the moment it is submitted through to completion.",
        },
      ],
      notes: [
        {
          icon: "mdi-shield-lock-outline",
          name: "Privacy and Security",
          text: "Your safety and privacy matter to us. Our Privacy Policy sets out how we handle your data and keep the app secure.",
        },
        {
          icon: "mdi-update",
          name: "App Updates",
          text: "We are always improving Sarpa. Check back regularly for new features and refinements.",
        },
        {
          icon: "mdi-message-draw",
          name: "Feedback",
          text: "Your feedback makes Sarpa better. Share your thoughts, suggestions or any issue you run into.",
        },
        {
          icon: "mdi-account-group-outline",
          name: "Community Guidelines",
          text: "Help keep this a positive space for users and volunteers, so everyone's experience stays safe and respectful.",
        },
      ],
    };
  },
};
</script>

<style scoped>
/* ================= shell =================
   Same emerald system as the rest of the portal. */
.support-shell {
  --accent: #10b981;
  --ink: #047857;
  --tint: #f0fdf9;
  --tint-line: #d2efe3;
  --badge: #d1fae5;
  --line: #e7eef3;
  --muted: #64748b;
  --shadow: 0 1px 2px rgba(15, 23, 42, 0.04), 0 8px 24px -12px rgba(15, 23, 42, 0.1);

  background: #f6f8fb;
  padding: 28px 32px 40px 32px;
}
/*
 * The panels span the page like every other screen in the portal - a centred
 * column left wide empty gutters on a desktop monitor. Line length is held in
 * check on the prose itself (in ch, below) rather than by narrowing everything.
 */
.support-wrap {
  width: 100%;
}

/* ================= typography ================= */
.cardtitle {
  font-family: poppinssemibold, poppinsregular, sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: #1a2e23;
  letter-spacing: -0.1px;
}
.subtext {
  font-family: poppinsregular, sans-serif;
  font-size: 12.5px;
  color: var(--muted);
  line-height: 1.45;
}

/* ================= hero ================= */
.support-hero {
  position: relative;
  overflow: hidden;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 16px;
  padding: 26px 26px 26px 30px;
  border: 1px solid var(--tint-line);
  border-radius: 18px;
  background: linear-gradient(135deg, #ffffff 0%, #f7fdfa 55%, #ecfdf5 100%);
  box-shadow: var(--shadow);
}
.hero-rail {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: linear-gradient(180deg, var(--accent) 0%, var(--ink) 100%);
}
.hero-badge {
  flex: 0 0 auto;
  width: 50px;
  height: 50px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #0ea97a 0%, var(--ink) 100%);
  box-shadow: 0 8px 20px -8px rgba(5, 150, 105, 0.55);
}
.hero-badge .v-icon {
  color: #ffffff !important;
}
.hero-copy {
  flex: 1 1 320px;
  min-width: 0;
}
.hero-eyebrow {
  font-family: poppinsmedium, poppinsregular, sans-serif;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.7px;
  text-transform: uppercase;
  color: var(--ink);
}
.hero-title {
  margin: 3px 0 0 0;
  font-family: poppinssemibold, poppinsregular, sans-serif;
  font-size: 26px;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: -0.6px;
  color: #1a2e23;
}
.hero-lead {
  margin: 8px 0 0 0;
  max-width: 62ch;
  font-family: poppinsregular, sans-serif;
  font-size: 13.5px;
  line-height: 1.6;
  color: var(--muted);
}
.hero-mail {
  flex: 0 0 auto;
  align-self: center;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 16px;
  border: 1px solid var(--tint-line);
  border-radius: 999px;
  background: #ffffff;
  font-family: poppinsmedium, poppinsregular, sans-serif;
  font-size: 12.5px;
  color: var(--ink) !important;
  text-decoration: none;
  white-space: nowrap;
  transition: background 0.25s ease, border-color 0.25s ease;
}
.hero-mail .v-icon {
  color: var(--accent) !important;
}
.hero-mail:hover {
  background: var(--tint);
  border-color: var(--accent);
}

/* ================= panels ================= */
.panel {
  margin-top: 20px;
  padding: 22px 24px 24px 24px;
  border: 1px solid var(--line);
  border-radius: 18px;
  background: #ffffff;
  box-shadow: var(--shadow);
}
.panel-head {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding-bottom: 16px;
  margin-bottom: 18px;
  border-bottom: 1px solid var(--line);
}
.panel-icon {
  flex: 0 0 auto;
  width: 38px;
  height: 38px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--badge);
}
.panel-icon .v-icon {
  color: var(--ink) !important;
}
.panel-copy {
  flex: 1 1 auto;
  min-width: 0;
  padding-top: 2px;
}

/* ================= features ================= */
.feature-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 12px;
}
.help-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 12px;
}
.feature-cell,
.help-cell {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid var(--line);
  border-radius: 13px;
  background: #fcfefd;
  transition: border-color 0.25s ease, background 0.25s ease;
}
.feature-cell:hover,
.help-cell:hover {
  border-color: var(--tint-line);
  background: var(--tint);
}
.feature-icon {
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--tint-line);
  background: #ffffff;
}
.feature-icon .v-icon {
  color: var(--ink) !important;
}
.feature-body {
  min-width: 0;
}
.feature-name {
  font-family: poppinssemibold, poppinsregular, sans-serif;
  font-size: 13.5px;
  color: #1a2e23;
  letter-spacing: -0.1px;
}
.feature-text {
  /* the cells are wide now; hold the measure so lines stay readable */
  max-width: 70ch;
  margin-top: 4px;
  font-family: poppinsregular, sans-serif;
  font-size: 12.5px;
  line-height: 1.55;
  color: var(--muted);
}
.mail-chip {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  max-width: 100%;
  margin-top: 10px;
  padding: 7px 14px;
  border: 1px solid var(--tint-line);
  border-radius: 999px;
  background: #ffffff;
  font-family: poppinsmedium, poppinsregular, sans-serif;
  font-size: 12px;
  color: var(--ink) !important;
  text-decoration: none;
  transition: background 0.25s ease, border-color 0.25s ease;
}
.mail-chip .v-icon {
  color: var(--accent) !important;
}
/* break between words, never mid-word */
.mail-text {
  min-width: 0;
  overflow-wrap: anywhere;
}
.mail-chip:hover {
  background: var(--tint);
  border-color: var(--accent);
}

/* ================= notes ================= */
.note-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 18px 26px;
}
.note-cell {
  min-width: 0;
}
.note-head {
  display: flex;
  align-items: center;
  gap: 8px;
}
.note-head .v-icon {
  color: var(--accent) !important;
}
.note-name {
  font-family: poppinssemibold, poppinsregular, sans-serif;
  font-size: 13.5px;
  color: #1a2e23;
}
.note-text {
  max-width: 70ch;
  margin: 6px 0 0 0;
  font-family: poppinsregular, sans-serif;
  font-size: 12.5px;
  line-height: 1.6;
  color: var(--muted);
}

/* ================= closing ================= */
.closing {
  max-width: 100ch;
  margin: 22px 0 0 0;
  padding: 16px 20px;
  border-left: 3px solid var(--accent);
  border-radius: 0 12px 12px 0;
  background: var(--tint);
  font-family: poppinsregular, sans-serif;
  font-size: 13px;
  line-height: 1.6;
  color: #3f5c4e;
}

@media (max-width: 600px) {
  .support-shell {
    padding: 20px 16px 28px 16px;
  }
  .support-hero {
    padding: 22px 18px 22px 22px;
  }
  .hero-title {
    font-size: 22px;
  }
  .hero-mail {
    align-self: flex-start;
  }
  .panel {
    padding: 20px 16px 20px 16px;
  }
}
</style>
